/**
 * The two answers a new account owes before the app opens: a date of birth and
 * a name. Both are asked AFTER the identity is proven (`lib/auth.ts` sends
 * neither with the code), exactly as the phone app does, and for the same
 * reasons: the age screen can only be enforced against an address the server
 * knows is somebody's, and an existing account on a new browser is never asked
 * again - the fact is on the row, not on the device.
 *
 *   PUT   /v1/me/age/   { date_of_birth }   200 | 403 age_too_young | 409 age_already_set
 *   PATCH /v1/me/       { display_name }    -> the user row
 */
import { api, ApiError } from './api';
import { getSession, setSession, type Session } from './session';

export type DobOutcome =
  | { ok: true }
  /** FINAL. The server has now locked the address, so reopening the page
   *  cannot shake it and retrying is pointless. */
  | { ok: false; refused: true }
  /** Not a refusal: a bad connection must not read as one. */
  | { ok: false; refused: false };

/**
 * Declare a date of birth for an account that has none (`YYYY-MM-DD`).
 *
 * Always asks the SERVER, even for a date that is plainly too young. The
 * phone app skips the request when its own check fails, which is a courtesy
 * that costs the enforcement: the block that makes a refusal stick
 * (`accounts/age_gate.py`) is written by the server's 403, so an answer
 * refused only on the client leaves the account open to a second try with a
 * different year.
 *
 * The server accepts a date exactly once; a second call is a 409, which here
 * means the row already has one - the goal this call exists for - and counts
 * as done.
 */
export async function submitDateOfBirth(iso: string): Promise<DobOutcome> {
  try {
    await api('/v1/me/age/', { method: 'PUT', body: { date_of_birth: iso } });
    return { ok: true };
  } catch (err) {
    if (err instanceof ApiError) {
      const code = (err.body as { code?: unknown } | undefined)?.code;
      if (code === 'age_too_young' || code === 'age_blocked') return { ok: false, refused: true };
      if (err.status === 409 || code === 'age_already_set') return { ok: true };
    }
    return { ok: false, refused: false };
  }
}

/** `PATCH /v1/me/` - the per-user endpoint. */
export async function saveDisplayName(
  name: string,
): Promise<{ display_name: string; avatar_url: string }> {
  const res = await api<{ display_name?: string; avatar_url?: string }>('/v1/me/', {
    method: 'PATCH',
    body: { display_name: name },
  });
  return { display_name: res?.display_name ?? name, avatar_url: res?.avatar_url ?? '' };
}

/** The same session with nothing owed. */
export function withoutOnboarding(session: Session): Session {
  const next = { ...session };
  delete next.onboarding;
  return next;
}

/**
 * Record that part of the profile step is done, in the stored session as well
 * as the one in hand. A date of birth that has been accepted must not be sent
 * again after a name that failed to save (it would be a 409), and closing the
 * tab between the two must not ask for it a second time.
 */
export function settle(
  session: Session,
  done: { name?: boolean; dob?: boolean },
  user?: { display_name: string; avatar_url: string },
): Session {
  const owed = {
    name: session.onboarding ? session.onboarding.name && !done.name : false,
    dob: session.onboarding ? session.onboarding.dob && !done.dob : false,
  };
  const base: Session = user ? { ...session, user: { ...session.user, ...user } } : session;
  const next = owed.name || owed.dob ? { ...base, onboarding: owed } : withoutOnboarding(base);
  // Only the signed-in session in storage is touched: a session this call
  // was handed after a sign-out must not be written back.
  if (getSession()?.token === session.token) setSession(next);
  return next;
}
